import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Mail, ShieldCheck } from "lucide-react";

export default function Login() {
  const { requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await requestOtp(email.trim());
      toast.success("Code sent. Check your inbox.");
      setStep("otp");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send code");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (val) => {
    const c = typeof val === "string" ? val : code;
    if (c.length !== 6 || busy) return;
    setBusy(true);
    try {
      await verifyOtp(email.trim(), c);
      toast.success("Welcome to Kippu");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      {/* Left: brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white login-glow overflow-hidden">
        <div className="relative z-10">
          <div className="inline-flex items-end gap-4">
            <img
              src="/brand/kippu-logo.svg"
              alt="Kippu"
              className="h-20 w-auto select-none rounded-md"
              draggable={false}
            />
            <div className="flex flex-col leading-none pb-2">
              <span className="text-[11px] uppercase tracking-[0.28em] text-slate-300">BY</span>
              <span className="text-[13px] uppercase tracking-[0.28em] text-slate-100 font-medium mt-1.5">ZEFFICACY</span>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h2 className="font-heading text-4xl font-bold leading-tight mb-4">
            One inbox.<br /> Every channel. <br /> Zero chaos.
          </h2>
          <p className="text-slate-300 text-sm leading-relaxed">
            Live Chat, WhatsApp, Social, Phone, Email, Forms — Kippu unifies
            customer conversations into a single, calm, scannable workspace
            built for support teams that ship.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-3 text-xs">
            {[
              "Omnichannel ticketing",
              "Hierarchical categories",
              "SLA tracking",
              "Automations engine",
              "Role-based access",
              "Knowledge base",
            ].map((f) => (
              <div key={f} className="flex items-center gap-2 text-slate-300">
                <ShieldCheck className="w-3.5 h-3.5 text-indigo-300" />
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-slate-400">
          © 2026 Zefficacy. All rights reserved.
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-sm">
          {step === "email" ? (
            <form onSubmit={sendCode} data-testid="login-form-email-step">
              <div className="lg:hidden mb-8 inline-flex items-end gap-3">
                <img
                  src="/brand/kippu-mark.svg"
                  alt="Kippu"
                  className="h-11 w-auto rounded-md select-none"
                  draggable={false}
                />
                <div className="flex flex-col leading-none pb-1.5">
                  <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500">BY</span>
                  <span className="text-xs uppercase tracking-[0.24em] text-slate-700 font-medium mt-1">ZEFFICACY</span>
                </div>
              </div>

              <h1 className="font-heading text-3xl font-semibold text-slate-900 tracking-tight">
                Sign in
              </h1>
              <p className="text-sm text-slate-500 mt-2 mb-8">
                Enter your work email. We'll send a 6-digit verification code.
              </p>

              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Work email
              </Label>
              <div className="mt-2 relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  required
                  autoFocus
                  className="pl-9 h-11"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <Button
                type="submit"
                disabled={busy || !email}
                className="w-full mt-6 h-11 bg-indigo-600 hover:bg-indigo-700"
                data-testid="login-send-code-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send code"}
              </Button>

              <p className="text-xs text-slate-400 mt-8 leading-relaxed">
                Don't have an account? Ask your administrator to invite you.
              </p>
            </form>
          ) : (
            <div data-testid="login-form-otp-step">
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setCode("");
                }}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-6"
                data-testid="login-back-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Change email
              </button>

              <h1 className="font-heading text-3xl font-semibold text-slate-900 tracking-tight">
                Check your inbox
              </h1>
              <p className="text-sm text-slate-500 mt-2 mb-8">
                We sent a 6-digit code to <span className="font-medium text-slate-700">{email}</span>. It expires in 10 minutes.
              </p>

              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Verification code
              </Label>
              <div className="mt-3 flex justify-center" data-testid="login-otp-input">
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={setCode}
                  onComplete={verify}
                  pattern="^\d+$"
                  pasteTransformer={(pasted) => pasted.replace(/\D/g, "").slice(0, 6)}
                  autoComplete="one-time-code"
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg font-semibold" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                onClick={verify}
                disabled={busy || code.length !== 6}
                className="w-full mt-8 h-11 bg-indigo-600 hover:bg-indigo-700"
                data-testid="login-verify-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & continue"}
              </Button>

              <button
                type="button"
                onClick={sendCode}
                disabled={busy}
                className="text-xs text-indigo-600 hover:text-indigo-700 mt-6 block mx-auto"
                data-testid="login-resend-btn"
              >
                Didn't receive? Resend code
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
